import axios from '../axios';
import EventBus from 'eventing-bus';
import { all, takeEvery, call, put, select } from 'redux-saga/effects';

import { buildQueryParams } from '../../utils/queryBuilder';
import { setDealsTable, setSingleDeal, setPipelines, setDealStats, setDealViews, setPipelineBoard, setLoading, setPipelineById } from '.';

// Deal Sagas
function* getDealsTable({ payload }) {
  yield put(setLoading(true));
  const queryParams = buildQueryParams(payload);
  const { error, response } = yield call(getCall, `/deals/table?${queryParams}`);
  if (error) EventBus.publish("error", error['response']?.['data']?.['message'] || "Error fetching deals");
  else if (response) yield put(setDealsTable(response['data']['body']));
  yield put(setLoading(false));
}

function* getPipelineBoard({ payload }) {
  // if payload is empty then add filters from redux
  if(!payload) {
    // Get current filters from the Deals store
    const dealsState = yield select(state => state['Objects']['deals']);
    payload = { filters: dealsState['activeFilters'] || {} };
  }

  yield put(setLoading(true));
  const queryParams = buildQueryParams(payload);
  const { error, response } = yield call(getCall, `/deals/pipeline-board?${queryParams}`);
  if (error) EventBus.publish("error", error['response']?.['data']?.['message'] || "Error fetching deals");
  else if (response) yield put(setPipelineBoard(response['data']['body']));
  yield put(setLoading(false));
}

function* getSingleDeal({ payload }) {
  yield put(setLoading(true));
  const { error, response } = yield call(getCall, `/deals/get/${payload}`);
  if (error) EventBus.publish("error", error['response']?.['data']?.['message'] || "Error fetching deal");
  else if (response) yield put(setSingleDeal(response['data']['body']));
  yield put(setLoading(false));
}

// Removed createDeal, updateDeal, deleteDealById - now handled by Objects store saga

function* moveDealStage({ payload }) {
  const { id, stage, targetPosition, dealPositions } = payload;
  const requestPayload = { stage };

  // Include targetPosition if provided
  if (targetPosition !== undefined)
    requestPayload.targetPosition = targetPosition;
  
  // Include complete deal positions array if provided
  if (dealPositions && dealPositions.length > 0)
    requestPayload.dealPositions = dealPositions;
  
  const { error, response } = yield call(putCall, { path: `/deals/move-stage/${id}`, payload: requestPayload });
  if (error) EventBus.publish("error", error['response']?.['data']?.['message'] || 'Failed to move deal');
  else if (response) {
    // // Get current filters from the Deals store
    // const dealsState = yield select(state => state['Objects']['deals']);
    // const activeFilters = dealsState['activeFilters'] || {};

    // // Dispatch with the active filters to maintain consistency
    // yield put({ type: "GET_PIPELINE_BOARD", payload: { filters: activeFilters } });
  }
}

// Pipeline Sagas
function* getPipelines() {
  const { error, response } = yield call(getCall, '/deals/pipelines');
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error fetching pipelines');
  else if (response) yield put(setPipelines(response['data']['body']));
}

function* getPipelineById({ payload }) {
  const { error, response } = yield call(getCall, `/deals/pipelines/${payload}`);
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error fetching pipeline');
  else if (response) yield put(setPipelineById(response['data']['body']));
}

function* createPipeline({ payload }) {
  const { error, response } = yield call(postCall, { path: '/deals/pipelines', payload });
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to create pipeline');
  else if (response) {
    EventBus.publish('success', 'Pipeline created successfully');
    yield put({ type: 'GET_PIPELINES' });
  }
}

function* updatePipeline({ payload }) {
  const { id, ...data } = payload;
  const { error, response } = yield call(putCall, { path: `/deals/pipelines/${id}`, payload: data });
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to update pipeline');
  else if (response) {
    EventBus.publish('success', 'Pipeline updated successfully');
    yield put({ type: 'GET_PIPELINES' });
    yield put({ type: 'GET_PIPELINE_BOARD' });
  }
}

function* deletePipeline({ payload }) {
  const { error, response } = yield call(deleteCall, `/deals/pipelines/${payload}`);
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to delete pipeline');
  else if (response) {
    EventBus.publish('success', 'Pipeline deleted successfully');
    yield put({ type: 'SET_PIPELINE_SETTINGS_MODAL', payload: false });

    // Fetch updated pipelines
    const { error: pipelinesError, response: pipelinesResponse } = yield call(getCall, '/deals/pipelines');
    if (!pipelinesError && pipelinesResponse) {
      const pipelines = pipelinesResponse['data']['body'] || [];
      yield put(setPipelines(pipelines));
      // Select first available pipeline or null if none
      const firstPipeline = pipelines[0] || null;
      yield put({ type: 'SET_CURRENT_PIPELINE', payload: firstPipeline });
      // Refresh pipeline board if a pipeline exists
      if (firstPipeline) yield put({ type: 'GET_PIPELINE_BOARD', payload: { filters: { pipelineId: firstPipeline['_id'] } } });
      else yield put(setPipelineBoard({ stages: [] }));
    }
  }
}

// Statistics Sagas
function* getDealStats({ payload = {} }) {
  const queryParams = new URLSearchParams();
  if (payload.startDate) queryParams.append('startDate', payload.startDate);
  if (payload.endDate) queryParams.append('endDate', payload.endDate);
  if (payload.ownerId) queryParams.append('ownerId', payload.ownerId);
  if (payload.pipeline) queryParams.append('pipeline', payload.pipeline);

  const { error, response } = yield call(getCall, `/deals/stats?${queryParams}`);
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error fetching statistics');
  else if (response) yield put(setDealStats(response['data']['body']));
}

// Import/Export Sagas
function* importDeals({ payload }) {
  const { error, response } = yield call(postCall, { path: '/deals/import', payload });
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Import failed');
  else if (response) EventBus.publish('success', 'Deals imported successfully');
}

function* exportDeals({ payload }) {
  const { error, response } = yield call(getCall, `/deals/export?${new URLSearchParams(payload)}`);
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Export failed');
  else if (response) {
    // Handle file download
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `deals-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    EventBus.publish('success', 'Deals exported successfully');
  }
}

// View Sagas
function* getDealViews() {
  const { error, response } = yield call(getCall, '/view/get?objectType=deals');
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error fetching views');
  else if (response) yield put(setDealViews(response['data']['body']));
}

function* createDealView({ payload }) {
  const { error, response } = yield call(postCall, {
    path: '/view/create',
    payload: { ...payload, objectType: 'deals' }
  });
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to create view');
  else if (response) {
    EventBus.publish('success', 'View created successfully');
    yield put({ type: 'GET_DEAL_VIEWS' });
  }
}

// Bulk Operations
function* bulkCreateDeals({ payload }) {
  const { error, response } = yield call(postCall, { path: '/deals/bulk-create', payload });
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Bulk create failed');
  else if (response) {
    EventBus.publish('success', `${response['data']['body']['created']} deals created successfully`);
  }
}

// Removed bulkUpdateDeals, bulkDeleteDeals - now handled by Objects store saga

// Tag Sagas

// Root Saga
export default function* dealsSaga() {
  yield all([
    yield takeEvery('GET_DEALS_TABLE', getDealsTable),
    yield takeEvery('GET_SINGLE_DEAL', getSingleDeal),
    yield takeEvery('MOVE_DEAL_STAGE', moveDealStage),
    yield takeEvery('GET_PIPELINES', getPipelines),
    yield takeEvery('GET_PIPELINE_BY_ID', getPipelineById),
    yield takeEvery('CREATE_PIPELINE', createPipeline),
    yield takeEvery('UPDATE_PIPELINE', updatePipeline),
    yield takeEvery('DELETE_PIPELINE', deletePipeline),
    yield takeEvery('GET_DEAL_STATS', getDealStats),
    yield takeEvery('IMPORT_DEALS', importDeals),
    yield takeEvery('EXPORT_DEALS', exportDeals),
    yield takeEvery('GET_DEAL_VIEWS', getDealViews),
    yield takeEvery('CREATE_DEAL_VIEW', createDealView),
    yield takeEvery('BULK_CREATE_DEALS', bulkCreateDeals),
    yield takeEvery('GET_PIPELINE_BOARD', getPipelineBoard),
  ]);
}

function postCall({ path, payload }) {
  return axios
    .post(path, payload)
    .then(response => ({ response }))
    .catch(error => {
      if (error.response?.status === 401) EventBus.publish("tokenExpired");
      return { error };
    });
}

function getCall(path) {
  return axios
    .get(path)
    .then(response => ({ response }))
    .catch(error => {
      if (error.response?.status === 401) EventBus.publish("tokenExpired");
      return { error };
    });
}

function deleteCall(path) {
  return axios
    .delete(path)
    .then(response => ({ response }))
    .catch(error => {
      if (error.response?.status === 401) EventBus.publish("tokenExpired");
      return { error };
    });
}

function putCall({ path, payload }) {
  return axios
    .put(path, payload)
    .then(response => ({ response }))
    .catch(error => {
      if (error.response?.status === 401) EventBus.publish("tokenExpired");
      return { error };
    });
}

function postFileCall({ path, payload }) {
  return axios
    .post(path, payload, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    .then(response => ({ response }))
    .catch(error => {
      if (error.response?.status === 401) EventBus.publish("tokenExpired");
      return { error };
    });
}